import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Modal,
  ScrollView,
} from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AdminTagChipPicker } from './AdminTagChipPicker';
import type { CorkBoardTag, CorkBoardThreadSummary } from '@/types/residents/corkBoard';

interface EditThreadTagsModalProps {
  visible: boolean;
  thread: CorkBoardThreadSummary | null;
  availableTags: CorkBoardTag[];
  isSaving: boolean;
  onSave: (threadId: string, tagIds: number[]) => void;
  onClose: () => void;
}

export const EditThreadTagsModal: React.FC<EditThreadTagsModalProps> = memo(
  ({ visible, thread, availableTags, isSaving, onSave, onClose }) => {
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

    const isAnnouncement = thread?.isAnnouncement ?? false;

    const categoryTags = useMemo(
      () => availableTags.filter((t) => t.slug !== 'announcements'),
      [availableTags],
    );

    const announcementsTagId = useMemo(
      () => availableTags.find((t) => t.slug === 'announcements')?.id ?? null,
      [availableTags],
    );

    useEffect(() => {
      if (visible && thread) {
        const tagIds = thread.tags.map((t) => Number(t.id));
        if (announcementsTagId) {
          setSelectedTagIds(tagIds.filter((id) => id !== announcementsTagId));
        } else {
          setSelectedTagIds(tagIds);
        }
      }
    }, [visible, thread, announcementsTagId]);

    const handleToggleTag = useCallback((tagId: number) => {
      setSelectedTagIds((prev) => {
        if (prev.includes(tagId)) {
          return prev.filter((id) => id !== tagId);
        }
        return [tagId];
      });
    }, []);

    const handleSave = useCallback(() => {
      if (isSaving || !thread) return;
      if (!isAnnouncement && selectedTagIds.length === 0) return;
      const finalTagIds = [...selectedTagIds];
      if (isAnnouncement && announcementsTagId && !finalTagIds.includes(announcementsTagId)) {
        finalTagIds.push(announcementsTagId);
      }
      onSave(thread.id, finalTagIds);
    }, [isSaving, thread, isAnnouncement, selectedTagIds, announcementsTagId, onSave]);

    const handleOverlayPress = useCallback(() => {
      if (!isSaving) onClose();
    }, [isSaving, onClose]);

    const isValid = isAnnouncement || selectedTagIds.length === 1;

    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={isSaving ? undefined : onClose}>
        <Pressable style={styles.overlay} onPress={handleOverlayPress}>
          <Pressable style={styles.modalContainer} onPress={(e) => e.stopPropagation()}>
            {/* Header */}
            <View style={styles.header}>
              <View>
                <Text style={styles.headerTitle}>Edit Thread Tags</Text>
                {thread && (
                  <Text style={styles.headerSubtitle} numberOfLines={1}>
                    {thread.title}
                  </Text>
                )}
              </View>
              <TouchableOpacity
                onPress={onClose}
                disabled={isSaving}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <View style={isSaving ? styles.closeButtonDisabled : undefined}>
                  <SVG_COMPONENTS.CANCEL_X width={18} height={18} color={ADMIN_COLORS.TEXT.SECONDARY} />
                </View>
              </TouchableOpacity>
            </View>

            {/* Body */}
            <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
              {isAnnouncement && (
                <Text style={styles.autoTagNote}>
                  Announcements tag is applied automatically
                </Text>
              )}
              <AdminTagChipPicker
                tags={categoryTags}
                selectedTagIds={selectedTagIds}
                onToggleTag={handleToggleTag}
                label={isAnnouncement ? 'Category tag (optional)' : 'Select a tag'}
              />
            </ScrollView>

            {/* Footer */}
            <View style={styles.footer}>
              <TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={isSaving}>
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveButton, (!isValid || isSaving) && styles.saveButtonDisabled]}
                onPress={handleSave}
                disabled={!isValid || isSaving}
              >
                <Text style={styles.saveButtonText}>{isSaving ? 'Saving...' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    );
  },
);

EditThreadTagsModal.displayName = 'EditThreadTagsModal';

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '90%',
    maxWidth: 480,
    maxHeight: '80%',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    gap: 12,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  headerSubtitle: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    marginTop: 2,
    maxWidth: 320,
  },
  closeButtonDisabled: {
    opacity: 0.3,
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    padding: 20,
    gap: 12,
  },
  autoTagNote: {
    fontSize: 12,
    fontStyle: 'italic',
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
  },
  cancelButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER_SECONDARY,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  saveButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BUTTON.PRIMARY_BACKGROUND,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.BUTTON.PRIMARY_TEXT,
  },
});
